import {
  ColActionId,
  IoniconsOutline,
  ListData,
  NavItem,
  NavItemsId,
  PermissionDialog,
  PtEntity,
  PtUiAppRoutes,
  TableActionData,
  TemplateDocumentType,
  UiDocumentItem,
} from '@pt/pt-models';
import {
  DocumentItemUtils,
  PermissionManager,
  PtCoreActions,
  PtCoreSelectors,
} from '@pt/pt-core';
import { Store } from '@ngrx/store';
import { ActivatedRoute } from '@angular/router';
import { SubmitDownloadRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/SubmitDownloadRequest';
import { UpsertUserColumnRequest } from 'it/primatoo/openapi/api/org/schemas/common/UpsertUserColumnRequest';
import { TranslateService } from '@ngx-translate/core';
import { BusinessProjectManagementService } from '@pt/api-client';
import { DocumentItem } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listdocumentresponse/DocumentItem';
import { DeleteService, DialogService } from '../services';
import { AppActionsBuilder } from '../utils';
import { ValidateDocumentRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/ValidateDocumentRequest';
import { TreatmentType } from 'it/primatoo/openapi/api/org/schemas/treatmentdoc/TreatmentType';
import {
  AiAssessmentActions,
  AppAssessmentActions,
  CovidAssessmentActions,
  TreatmentActions,
} from '../documents';

export abstract class AbstractDocumentPage {
  protected currentFilter: string;
  public detailsOpened = false;
  public currentDocId: number;
  public IconWarning = IoniconsOutline.warning;
  public permissionDialog?: PermissionDialog;
  public documentListData: ListData<DocumentItem>;
  public rightAction: NavItem[] = [];
  public ColActionId = ColActionId;
  public reindexRunning = false;
  public loading = false;
  protected firstLoad = false;

  public loginResponse$ = this.store.select(PtCoreSelectors.loginResponse);

  protected constructor(
    protected store: Store,
    protected deleteService: DeleteService,
    protected activatedRoute: ActivatedRoute,
    protected translateService: TranslateService,
    protected permissionManager: PermissionManager,
    protected service: BusinessProjectManagementService,
    protected dialogService: DialogService
  ) {
    const actionsBuilder = new AppActionsBuilder(
      this.translateService,
      this.permissionManager
    );
    this.rightAction = actionsBuilder.ofHeader(this.getEntity());
  }

  protected checkQueryParams(): void {
    const queryParams = this.activatedRoute.snapshot.queryParams;
    this.currentDocId = Number(queryParams.id);
    this.detailsOpened = Boolean(
      JSON.parse(queryParams.opened ? queryParams.opened : false)
    );
  }

  protected openDocument(
    documentId: number,
    path: PtUiAppRoutes,
    onBlankPage = false,
    queryParams?: Record<string, unknown>
  ): void {
    this.store.dispatch(
      PtCoreActions.navigateTo({
        route: [
          PtUiAppRoutes.BUSINESS_MANAGEMENT,
          path,
          PtUiAppRoutes.EDIT,
          documentId?.toString(),
        ],
        onBlankPage,
        queryParams: queryParams,
      })
    );
  }

  public onClickRefresh(): void {
    const id = this.currentDocId;
    this.currentDocId = null;
    setTimeout(() => {
      this.currentDocId = id;
    }, 500);
  }

  public completeClickAction(action: NavItem, documentItems: UiDocumentItem[]) {
    switch (action.id) {
      case NavItemsId.OBSOLETE:
        this.obsoleteDocuments();
        break;
      case NavItemsId.DOWNLOAD:
        this.downloadDocument(documentItems);
        break;
      case NavItemsId.RELOAD_BUTTON:
        this.loadDocuments(this.currentFilter);
        break;
      case NavItemsId.CREATE_BUTTON:
        this.createDocument();
        break;
      case NavItemsId.EXPORT_TABLE_BUTTON:
        this.exportTable();
        break;
    }
  }

  public filterData(filter?: string): void {
    this.loadDocuments(filter);
  }

  public clickTableActions(
    tableActionData: TableActionData<DocumentItem>
  ): void {
    this.manageEntityAction(
      tableActionData?.action?.id,
      DocumentItemUtils.convertDocument(tableActionData?.row)
    );
  }

  public downloadDocument(documents: Array<UiDocumentItem>): void {
    DocumentItemUtils.checkDownloadDoc(documents).subscribe(
      (response: SubmitDownloadRequest | any) => {
        if ('downloadType' in response) {
          this.store.dispatch(
            PtCoreActions.documentTableDownloadRequest({
              request: response,
              templateDocumentType: this.getTemplateDocumentType(),
            })
          );
        }
      }
    );
  }

  public settings() {
    this.reindexRunning = true;
    this.dialogService.runDocumentSettingsDialog().subscribe(
      (response) => {
        switch (response) {
          case undefined:
          case null:
          case 'CLOSE':
            this.reindexRunning = false;
            break;
          case 'REINDEX':
            this.service.reindex().subscribe(
              () => {
                this.loadDocuments(this.currentFilter);
                this.reindexRunning = false;
              },
              () => {
                this.loadDocuments(this.currentFilter);
                this.reindexRunning = false;
              }
            );
            break;
          case 'RESETCOL':
            this.onResetColumns();
            this.reindexRunning = false;
            break;
          default:
            this.reindexRunning = false;
            break;
        }
      },
      () => {
        this.reindexRunning = false;
      }
    );
  }

  protected snapshotDocument(docId: number, docName: string) {
    if (docId && docName) {
      this.dialogService
        .runCreateSnapshotDialog(docName)
        .subscribe((request?: ValidateDocumentRequest) => {
          if (request) {
            this.service.snapshot(docId, request).subscribe(
              () => {
                this.store.dispatch(
                  PtCoreActions.showOperationSuccessNotification()
                );

                switch (this.getEntity()) {
                  case 'aiAssessment':
                    this.store.dispatch(
                      AiAssessmentActions.createSnapshotSuccess()
                    );
                    break;
                  case 'appAssessment':
                    this.store.dispatch(
                      AppAssessmentActions.createSnapshotSuccess()
                    );
                    break;
                  case 'covidAssessment':
                    this.store.dispatch(
                      CovidAssessmentActions.createSnapshotSuccess()
                    );
                    break;
                  case 'treatmentsResponsible':
                  case 'treatmentsHolder':
                  case 'treatmentsSubResponsible':
                  case 'projectCards':
                    this.store.dispatch(
                      TreatmentActions.createSnapshotSuccess()
                    );
                    break;
                }
              },
              () => {
                this.store.dispatch(
                  PtCoreActions.showOperationFailNotification()
                );
              }
            );
          }
        });
    }
  }

  abstract getTemplateDocumentType(): TemplateDocumentType;

  abstract loadDocuments(filter?: string): void;

  abstract manageEntityAction(
    colActionId: ColActionId,
    documentItem: UiDocumentItem
  ): void;

  protected abstract publishDocument(docId: number): void;

  protected abstract reopenDocument(docId: number): void;

  protected abstract duplicateDocument(
    document: UiDocumentItem,
    internalIdEditable: boolean
  ): void;

  protected obsoleteDocuments() {
    switch (this.getEntity()) {
      case 'treatmentsHolder':
        this.dialogService.runObsoleteTreatmentDialog(
          TreatmentType.HOLDER,
          'treatmentsHolder',
          (e, docs) => this.deleteService.deleteDocument(e, docs)
        );
        break;
      case 'treatmentsResponsible':
        this.dialogService.runObsoleteTreatmentDialog(
          TreatmentType.RESPONSIBLE,
          'treatmentsResponsible',
          (e, docs) => this.deleteService.deleteDocument(e, docs)
        );
        break;
      case 'treatmentsSubResponsible':
        this.dialogService.runObsoleteTreatmentDialog(
          TreatmentType.SUB_RESPONSIBLE,
          'treatmentsSubResponsible',
          (e, docs) => this.deleteService.deleteDocument(e, docs)
        );
        break;
      case 'projectCards':
        this.dialogService.runObsoleteTreatmentDialog(
          TreatmentType.PROJECT,
          'projectCards',
          (e, docs) => this.deleteService.deleteDocument(e, docs)
        );
        break;
      default:
        console.warn(
          'Obsolete document non implemented for entity type ',
          this.getEntity()
        );
    }
  }

  abstract onResetColumns(): void;

  abstract onDetailsClickAction(colActionId: ColActionId): void;

  abstract onChangeUserColumnsVisible(columns: UpsertUserColumnRequest): void;

  protected abstract exportTable(): void;

  /**
   * App Assessment Category è usata SOLO per l'app Assessment
   * @param appAssessmentCategory
   * @protected
   */
  protected abstract createDocument(
    appAssessmentCategory?: 'PRIMATOO' | 'SUPPLIER' | 'INTERNAL'
  ): void;

  public abstract getEntity(): PtEntity;
}
